import {
  Children,
  cloneElement,
  isValidElement,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';

interface DisabledFormWrapperProps {
  children: ReactNode;
  disabled: boolean;
}

interface DisableableElementProps {
  children?: ReactNode;
  disabled?: boolean;
  readOnly?: boolean;
  style?: CSSProperties;
}

function isNativeFormElement(
  type: ReactElement<DisableableElementProps>['type'],
): type is 'button' | 'input' | 'select' | 'textarea' {
  return (
    type === 'button' ||
    type === 'input' ||
    type === 'select' ||
    type === 'textarea'
  );
}

function disableElement(element: ReactNode): ReactNode {
  if (!isValidElement<DisableableElementProps>(element)) {
    return element;
  }

  const children = element.props.children;
  const nextChildren =
    children === undefined ? children : Children.map(children, disableElement);

  if (!isNativeFormElement(element.type)) {
    return nextChildren === children
      ? element
      : cloneElement(element, { children: nextChildren });
  }

  return cloneElement(element, {
    disabled: true,
    readOnly: true,
    style: {
      ...element.props.style,
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    children: nextChildren,
  });
}

export default function DisabledFormWrapper({
  children,
  disabled,
}: DisabledFormWrapperProps) {
  if (!disabled) {
    return <>{children}</>;
  }

  return (
    <div aria-disabled="true" inert style={{ pointerEvents: 'none' }}>
      {Children.map(children, disableElement)}
    </div>
  );
}
